<template>
  <div class="h-full">
    <NuxtPwaManifest />
    <NuxtLoadingIndicator />
    <NuxtLayout
      id="app-main"
    >
      <!--
        Same hazard as the memberhub layout chrome: `pageLoading` can flip true
        during hydration when a page's onMounted calls a store action that
        pushes the page loader (vaultStore.show does this synchronously).
        Mounting a direct sibling of the still-suspended <NuxtPage> re-anchors
        the insert point and throws "insertBefore: ... not a child of this
        node", so keep the toggle inside an always-rendered wrapper.
      -->
      <div>
        <AtomsLoadingAnimation v-if="pageLoading" />
      </div>
      <NuxtPage />
    </NuxtLayout>
    <ClientOnly>
      <MoleculesNavNotificationSlider v-if="userLoggedIn && enableContentCardNotifications" />
    </ClientOnly>

    <!-- SNACKBARS CONTAINER -->
    <Teleport to="body">
      <div
        v-if="!_isEmpty(snackbars)"
        class="fixed left-0 bottom-0 z-[2147483002] pointer-events-none px-4 sm:pl-10 sm:pr-0 pb-4 sm:pb-10 flex flex-col gap-4"
      >
        <V2MoleculesSnackbar
          v-for="(snack, snackIndex) in snackbars"
          :key="snackIndex"
          v-bind="snack"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import { storeToRefs } from 'pinia'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import { isPaymentRoute } from './middleware/gtm-payment-guard.global'

const layoutsStore = useLayoutsStore()
const utmsStore = useUtmsStore()
const userStore = useUserStore()
const snackbarsStore = useSnackbarStore()
const { snackbars } = storeToRefs(snackbarsStore)

const { $tracking, $bus } = useNuxtApp()
const { devices } = deviceChecker()

const $router = useRouter()
const $route = useRoute()

const { enableContentCardNotifications } = useRuntimeConfig().public
const userLoggedIn = computed(() => userStore.isUserLoggedIn)

/*
 * Page loading status property
 * @type {ComputedRef<*>}
 */
const pageLoading = computed(() => layoutsStore.getPageLoaderStatus)

const html = import.meta.client && document.documentElement
const body = import.meta.client && document.querySelector('body')

// Initialize optimizely
const { $optimizelyClient } = useNuxtApp()

watch(devices.width, async () => {
  await nextTick()

  if (devices.isDesktop.value) {
    if (html.style.overflow === 'hidden') {
      html.style.overflow = ''
    }

    if (body.style.overflow === 'hidden') {
      body.style.overflow = ''
    }
  }
})

/**
 * check optimizely on every route change
 *
 * Optimizely is intentionally never initialised on payment routes: its script
 * uses eval() at runtime, which violates the strict CSP on those pages, and it
 * has no first-class disable API. The check is duplicated inside the plugin
 * itself for defence in depth, but gating here is the authoritative single
 * source of truth and avoids any race where the watcher fires before the
 * plugin's path-check runs.
 */
watch(() => $route.fullPath, async () => {
  utmsStore.GENERATE_UTMS($route.query)

  if (!import.meta.client) return
  if (isPaymentRoute($route.path) || isPaymentRoute(window.location.pathname)) return

  await $optimizelyClient.init()
}, {
  immediate: true,
  deep: true
})

watch(() => $route.fullPath, () => {
  $tracking.pageview(additionalParams.value)
})

/*
 | Trustpilot.
 |
 | Blocking on purpose. useJsonld() below reads trustScore/numberOfReviews, and the
 | AggregateRating must be present in the SSR HTML — deferring this would ship every
 | crawl with the hardcoded 4.6 / 9410 fallback from app/utils/jsonld-schema.js.
 |
 | Safe to block because /api/trustpilot/data is now an in-process cache read (~0.2ms on
 | a hit) that never awaits a refresh once warm, falls back to a committed seed when
 | upstream is down, and is bounded by a 2s timeout on the one pathological case: the
 | very first request after a Nitro process boots.
 |
 | The fetch lives in the store so there is exactly one call site and one useFetch key —
 | see TRUSTPILOT_DATA_KEY in app/stores/trustpilot.js. Previously app.vue and the store
 | each had their own auto-derived key, so they never deduped.
 */
const trustpilotStore = useTrustpilotStore()

await trustpilotStore.fetchTrustpilotData()

const additionalParams = ref({})

$bus.on('pageview', payload => {
  additionalParams.value = payload
})

/*
 * Global redirection handler
 */
$bus.off('redirection')
$bus.on('redirection', payload => {
  if (payload.path && (payload.path.includes('http'))) {
    window.location = payload.path
  } else if (payload.path && payload.path.includes('?')) {
    const [pathStr, queryStr] = payload.path.split('?')
    const query = { ...Object.fromEntries(new URLSearchParams(queryStr)), ...payload.query }
    $router.push({ path: pathStr, query })
  } else {
    $router.push(payload)
  }
})

function getScrollPosition () {
  const offset = window.pageYOffset || html.scrollTop

  layoutsStore.scrollPosition = offset
}

onMounted(() => {
  // Native app: the native splash is held (launchAutoHide: false) until the
  // dashboard signals ready and calls SplashScreen.hide() (see dashboard
  // onMounted). Failsafe: hide after a few seconds so the splash can never stick
  // if the dashboard never mounts.
  if (import.meta.client && Capacitor.isNativePlatform()) {
    setTimeout(() => {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => { /* already hidden */ })
    }, 4000)
  }

  $tracking.pageview(additionalParams.value)

  window.addEventListener('scroll', getScrollPosition)

  userStore.$patch(state => {
    const user = getLocal('auth_user')
    const token = getLocal('token')

    state.userProfile = user && user !== 'undefined' ? JSON.parse(user) : {}
    state.token = token && token !== 'undefined' ? JSON.parse(token) : {}
  })

  // Inject Splash screen for IOS devices
  if (import.meta.client && window.matchMedia('(display-mode: standalone)').matches) {
    useIosSplash('/images/pwa/cc-icon-512-rounded.png', '#ffffff') // Icon must be in public/
  }

  getGoogleAnalyticsClientId()
})

onUnmounted(() => window.removeEventListener('scroll', getScrollPosition))

async function getGoogleAnalyticsClientId () {
  while (!window.ga) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => requestAnimationFrame(resolve))
  }

  await wait(500)

  const clientId = window.ga.getAll().length > 0
    ? window.ga.getAll()[0].get('clientId')
    : ''

  utmsStore.utms.ga_client_id = clientId
}

useJsonld(() => {
  const {
    trustScore,
    numberOfReviews
  } = trustpilotStore.trustpilotData ?? {}

  return {
    '@context': 'https://schema.org',
    '@graph': [
      SCHEMA_WEBSITE,
      GENERATE_ORGANIZATION_SCHEMA({
        rating: trustScore,
        count: numberOfReviews?.total
      }),
      GENERATE_LOGO_SCHEMA()
    ]
  }
})
</script>
